import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Play, School, MapPin, Users, Star, Search, Filter, Maximize2, Minimize2, Heart } from 'lucide-react';
import SchoolsSection from '../components/SchoolsSection';
import RegionSchoolsTable from '../components/RegionSchoolsTable';
import schoolCache from '../utils/schoolCache';

const Schools = () => {
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [schools, setSchools] = useState([]);

    const toggleFullscreen = () => {
        setIsFullscreen(!isFullscreen);
    };

    // Load schools from cache
    useEffect(() => {
        const unsubscribe = schoolCache.subscribe((cacheData) => {
            setSchools(cacheData.schools || []);
        });

        // Load schools if not already loaded
        if (schoolCache.schools.length === 0) {
            schoolCache.loadSchools();
        } else {
            setSchools(schoolCache.schools);
        }

        return unsubscribe;
    }, []);

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Hero Video Section */}
            <section className="relative h-screen flex items-center justify-center overflow-hidden">
                {/* Background Video */}
                <div className="absolute inset-0 z-0 bg-gradient-to-br from-gray-800 to-gray-900">
                    <div className="relative w-full h-full overflow-hidden">
                        <iframe
                            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 min-w-full min-h-full w-auto h-auto"
                            src="https://www.youtube-nocookie.com/embed/JrLz8_nHbMs?autoplay=1&loop=1&playlist=JrLz8_nHbMs&mute=1&controls=0&showinfo=0&rel=0&modestbranding=1&iv_load_policy=3&fs=0&disablekb=1&start=0&enablejsapi=0&origin=https://amhas.org"
                            title="Gambia Schools Video"
                            frameBorder="0"
                            allow="autoplay; encrypted-media"
                            referrerPolicy="no-referrer-when-downgrade"
                            style={{
                                width: '177.77777778vh',
                                height: '100vh',
                                minWidth: '100vw',
                                minHeight: '56.25vw'
                            }}
                        ></iframe>
                    </div>
                    <div className="absolute inset-0 bg-black/20"></div>
                </div>

                {/* Hero Content */}
                <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                    >
                        <h1 className="text-5xl md:text-7xl font-bold mb-6">
                            Gambia Schools
                        </h1>
                        <p className="text-xl md:text-2xl text-primary-100 mb-8 max-w-3xl mx-auto">
                            Discover schools across The Gambia participating in AMHAS healthcare services.
                            Find the perfect educational institution for your child's health and learning journey.
                        </p>
                        <div className="flex items-center justify-center space-x-4 text-primary-200">
                            <div className="flex items-center space-x-2">
                                <School size={20} />
                                <span>200+ Schools</span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <MapPin size={20} />
                                <span>All Regions</span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <Users size={20} />
                                <span>50,000+ Students</span>
                            </div>
                        </div>

                    </motion.div>
                </div>

                {/* Scroll Indicator */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1, duration: 0.5 }}
                    className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10"
                >
                    <div className="w-6 h-10 border-2 border-white rounded-full flex justify-center">
                        <motion.div
                            animate={{ y: [0, 12, 0] }}
                            transition={{ duration: 1.5, repeat: Infinity }}
                            className="w-1 h-3 bg-white rounded-full mt-2"
                        />
                    </div>
                </motion.div>
            </section>

            {/* Schools Table Section */}
            <section className="py-4 bg-white">
                <div className="mx-5">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true }}
                    >
                        <RegionSchoolsTable schools={schools} />
                    </motion.div>
                </div>
            </section>

            {/* Separating Section - We Care Message */}
            <section className="relative h-[60vh] flex items-center justify-center overflow-hidden -mx-4 sm:-mx-6 lg:-mx-8">
                {/* Background Image */}
                <div className="absolute inset-0 z-0">
                    <img
                        src="/african-school-kid.jpg"
                        alt="African school children"
                        className="w-full h-full object-cover object-center"
                        style={{
                            minWidth: '100vw',
                            minHeight: '100vh',
                            objectPosition: 'center center'
                        }}
                    />
                    {/* Gradient Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-br from-black/50 via-black/30 to-black/50"></div>
                </div>

                {/* Animated Gradient Text */}
                <div className="relative z-10 text-center px-4 max-w-6xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                        className="space-y-6"
                    >
                        <motion.h2
                            initial={{ opacity: 0, scale: 0.9 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                            viewport={{ once: true }}
                            className="text-6xl md:text-8xl font-bold mb-8"
                        >
                            <span className="bg-gradient-to-r from-primary-400 via-accent-400 to-primary-600 bg-clip-text text-transparent animate-gradient-x">
                                We Care
                            </span>
                        </motion.h2>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.4 }}
                            viewport={{ once: true }}
                            className="space-y-6"
                        >
                            <motion.p
                                className="text-3xl md:text-4xl text-white font-semibold"
                                initial={{ opacity: 0, x: -50 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.6, delay: 0.6 }}
                                viewport={{ once: true }}
                            >
                                <span className="bg-gradient-to-r from-yellow-300 via-white to-yellow-300 bg-clip-text text-transparent animate-gradient-x">
                                    We Are Here
                                </span>
                            </motion.p>
                            <motion.p
                                className="text-2xl md:text-3xl text-white"
                                initial={{ opacity: 0, x: 50 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.6, delay: 0.8 }}
                                viewport={{ once: true }}
                            >
                                <span className="bg-gradient-to-r from-primary-200 via-white to-accent-200 bg-clip-text text-transparent animate-gradient-x">
                                    Anytime You Need Care
                                </span>
                            </motion.p>
                            <motion.p
                                className="text-2xl md:text-3xl text-white"
                                initial={{ opacity: 0, x: -50 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.6, delay: 1.0 }}
                                viewport={{ once: true }}
                            >
                                <span className="bg-gradient-to-r from-accent-200 via-white to-primary-200 bg-clip-text text-transparent animate-gradient-x">
                                    Have No Fear
                                </span>
                            </motion.p>
                            <motion.p
                                className="text-3xl md:text-4xl text-white font-bold"
                                initial={{ opacity: 0, scale: 0.9 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.6, delay: 1.2 }}
                                viewport={{ once: true }}
                            >
                                <span className="bg-gradient-to-r from-yellow-300 via-white to-primary-300 bg-clip-text text-transparent animate-gradient-x">
                                    We've Got You Covered
                                </span>
                            </motion.p>
                        </motion.div>

                        {/* Decorative Elements */}
                        <motion.div
                            initial={{ opacity: 0, scale: 0.8 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.8, delay: 0.6 }}
                            viewport={{ once: true }}
                            className="flex items-center justify-center space-x-8 mt-12"
                        >
                            <div className="w-20 h-1 bg-gradient-to-r from-transparent via-yellow-400 to-transparent"></div>
                            <div className="w-4 h-4 bg-yellow-400 rounded-full animate-pulse shadow-lg"></div>
                            <div className="w-20 h-1 bg-gradient-to-r from-transparent via-yellow-400 to-transparent"></div>
                        </motion.div>

                        {/* Call to Action */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 1.4 }}
                            viewport={{ once: true }}
                            className="mt-12"
                        >
                            <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-full px-6 py-3 border border-white/20">
                                <Heart size={20} className="text-yellow-400" />
                                <span className="text-white font-medium">Join thousands of families who trust AMHAS</span>
                            </div>
                        </motion.div>
                    </motion.div>
                </div>

                {/* Scroll Indicator */}
                <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ delay: 1, duration: 0.5 }}
                    viewport={{ once: true }}
                    className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10"
                >
                    <div className="w-6 h-10 border-2 border-white rounded-full flex justify-center">
                        <motion.div
                            animate={{ y: [0, 12, 0] }}
                            transition={{ duration: 1.5, repeat: Infinity }}
                            className="w-1 h-3 bg-white rounded-full mt-2"
                        />
                    </div>
                </motion.div>
            </section>

            {/* Schools Map and List Section */}
            <section className={`${isFullscreen ? 'fixed inset-0 z-50 bg-white' : 'relative'} transition-all duration-300`}>
                {/* Fullscreen Toggle */}
                <div className="absolute top-4 right-4 z-10">
                    <button
                        onClick={toggleFullscreen}
                        className="bg-white/90 backdrop-blur-sm rounded-full p-3 shadow-lg hover:bg-white transition-colors"
                        title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
                    >
                        {isFullscreen ? (
                            <Minimize2 size={20} className="text-gray-700" />
                        ) : (
                            <Maximize2 size={20} className="text-gray-700" />
                        )}
                    </button>
                </div>

                {/* Map Section - Reduced height by 50% */}
                <div className={`${isFullscreen ? 'h-screen' : 'h-[25vh]'} flex flex-col`}>
                    <SchoolsSection />
                </div>
            </section>
        </div>
    );
};

export default Schools;
